/**
 * DnsRecordDisplay - Shared component for displaying DNS records with copy functionality
 */
import React from 'react';
import { Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { DnsRecord } from '@/api/domainsApi';

interface DnsRecordDisplayProps {
  record: DnsRecord;
  nameLabel?: string;
  valueLabel?: string;
  /** Unique identifier for this record (for copy state management) */
  recordId?: string;
}

export const DnsRecordDisplay: React.FC<DnsRecordDisplayProps> = ({
  record,
  nameLabel = 'Name / Host',
  valueLabel = 'Value',
  recordId = '',
}) => {
  const { copy, isCopied } = useCopyToClipboard();
  const nameKey = `${recordId}-name`;
  const valueKey = `${recordId}-value`;

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-muted-foreground mb-2">{nameLabel}</label>
        <div className="flex items-center gap-2">
          <code className="flex-1 px-3 py-2.5 bg-foreground/5 rounded border border-border text-base font-mono text-foreground overflow-auto">
            {record.name}
          </code>
          <Button
            variant="tertiary"
            size="icon"
            className="h-10 w-10 flex-shrink-0"
            onClick={() => copy(record.name, nameKey)}
          >
            {isCopied(nameKey) ? (
              <Check className="h-4 w-4 text-success" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-muted-foreground mb-2">{valueLabel}</label>
        <div className="flex items-center gap-2">
          <code className="flex-1 px-3 py-2.5 bg-foreground/5 rounded border border-border text-base font-mono text-foreground overflow-auto">
            {record.value}
          </code>
          <Button
            variant="tertiary"
            size="icon"
            className="h-10 w-10 flex-shrink-0"
            onClick={() => copy(record.value, valueKey)}
          >
            {isCopied(valueKey) ? (
              <Check className="h-4 w-4 text-success" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * DnsRecordsListDisplay - Display multiple DNS records (e.g., A records for apex + www)
 */
interface DnsRecordsListDisplayProps {
  records: DnsRecord[];
  title?: string;
  description?: string;
}

export const DnsRecordsListDisplay: React.FC<DnsRecordsListDisplayProps> = ({
  records,
  title,
  description,
}) => {
  return (
    <div className="space-y-4">
      {title && <h4 className="font-medium text-sm">{title}</h4>}
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
      <div className="space-y-3">
        {records.map((record, index) => (
          <div key={`${record.type}-${record.name}-${index}`} className="p-3 bg-foreground/5 rounded-2xl">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 bg-foreground/10 text-foreground text-xs font-medium rounded">
                {record.type}
              </span>
              {record.ttl && (
                <span className="text-xs text-muted-foreground">TTL: {record.ttl}</span>
              )}
            </div>
            <DnsRecordDisplay
              record={record}
              recordId={`${record.type}-${index}`}
              nameLabel="Host / Name"
              valueLabel={record.type === 'A' ? 'IP Address' : 'Value'}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
